import { faPlus, faRemove } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import moment from "moment-timezone";
import Form, { List } from "rc-field-form";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import Button from "../../../../../../components/Button";
import Field from "../../../../../../components/Field";
import NumberInput from "../../../../../../components/Inputs/NumberInput";
import ResourceSelect from "../../../../../../components/Inputs/ResourceSelect";
import { generateErrorsConfigForForm } from "../../../../../../utils/helpers/errors";
import { getPathWithParams, getSettingsResourceSearchFilters, renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";
import { firstToUpper } from "../../../../../../utils/helpers/string";
import { useModuleAccess } from "../../../../../../utils/hooks/access";
import { useIsMasterCompany } from "../../../../../../utils/hooks/user";
import { useIsFieldDisabled } from "../../../../helpers/useIsFieldDisabled";
import { payCodeCalcFields } from "../../../CalculationGroup/helper";

const getOrdinal = (count) => {
    let ordinal = "";
    switch (count) {
        case 1: {
            ordinal = "first";
            break;
        }
        case 2: {
            ordinal = "second";
            break;
        }
        case 3: {
            ordinal = "third";
            break;
        }
        case 4: {
            ordinal = "fourth";
            break;
        }
        case 5: {
            ordinal = "fifth";
            break;
        }
        default: {
            ordinal = `${count}th`;
            break;
        }
    }
    return `${ordinal}-meal-requirement`;
}

const generatePayCodeValidator = (t, data) => {
    return ({ getFieldValue }) => ({
        validator({ fullField }, value) {
            if (!value) {
                return Promise.resolve();
            }
            const paths = data
                .flatMap(({ name: fieldName }) => {
                    return [
                        ["rules", fieldName, "violationPayCode"],
                        ["rules", fieldName, "payCode"],
                        ["rules", fieldName, "waivePayCode"],
                        ["rules", fieldName, "managerWaivePayCode"],
                    ];
                })
                .filter((path) => path.join(".") !== fullField);
            const usedPayCodeIds = paths.flatMap((path) => getFieldValue(path)?.id);
            const alreadyUsed = usedPayCodeIds.includes(value?.id);
            if (alreadyUsed) {
                return Promise.reject(new Error(t("pay-code-should-be-unique")));
            }
            return Promise.resolve();
        },
    });
}

function Rules({ form: parentForm, mode, error, values, close, goBack, hidden }) {
    const { t } = useTranslation();

    const isMasterCompany = useIsMasterCompany();
    const { access } = useModuleAccess("settings.payPolicies.calculationGroup");
    const disabled = useIsFieldDisabled({ ...access, mode });

    let form;
    const [componentForm] = Form.useForm();
    if (parentForm) {
        form = parentForm;
    } else {
        form = componentForm;
    }

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue({
            rules: values?.rules || [],
        });
    }, [form, values]);

    useEffect(() => {
        const fieldErrors = generateErrorsConfigForForm([], error);
        form.setFields(fieldErrors);
    }, [error, form]);

    const { canWaive, canManagerWaive, generateMultiplePremiumsForViolations } = values || {};

    return (
        <Form
            form={form}
            name="rules"
            hidden={hidden}
            className={classNames(!hidden && "d-flex flex-column overflow-hidden")}
        >
            <div className="overflow-auto flex-grow-1 mb-4">
                <List name="rules">
                    {(data, { add, remove }) => {
                        const payCodeFieldDependencies = data.flatMap(({ name: fieldName }) => {
                            return [
                                ["rules", fieldName, "violationPayCode"],
                                ["rules", fieldName, "payCode"],
                                ["rules", fieldName, "waivePayCode"],
                                ["rules", fieldName, "managerWaivePayCode"],
                            ];
                        });
                        return (
                            <div className="d-flex flex-column mb-4" style={{ overflowX: "hidden" }}>
                                {data.map(({ key, name }) => {
                                    return (
                                        <div
                                            key={key}
                                            className="mb-3 p-3 rounded-lg"
                                            style={{ border: "1px solid #e6e6e6" }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                <h3 className="mb-0">{t(getOrdinal(key + 1))}</h3>

                                                <FontAwesomeIcon
                                                    icon={faRemove}
                                                    size="lg"
                                                    className="text-danger cursor-pointer"
                                                    onClick={() => remove(name)}
                                                />
                                            </div>

                                            <div>
                                                <p className="mb-2">{t("after-how-many-hours-is-the-break-required")}</p>

                                                <Row>
                                                    <Col lg={6}>
                                                        <Field
                                                            label={t("duration")}
                                                            name={[name, "duration"]}
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: t("required-duration")
                                                                }
                                                            ]}
                                                        >
                                                            {({ value: _value, onChange }) => {
                                                                let value;
                                                                if (!isNaN(Number(_value))) {
                                                                    value = Number(moment.duration(_value, "minutes").asHours().toFixed(2));
                                                                }
                                                                return (
                                                                    <div className="d-flex">
                                                                        <NumberInput
                                                                            value={value}
                                                                            onChange={(e) => {
                                                                                let value = e.target.value;
                                                                                if (!isNaN(Number(_value))) {
                                                                                    value = moment.duration(value, "hours").asMinutes();
                                                                                }
                                                                                onChange(value)
                                                                            }}
                                                                            className="border-right-0"
                                                                            style={{
                                                                                borderRadius: "0.25rem 0 0 0.25rem",
                                                                            }}
                                                                        />
                                                                        <div
                                                                            className="d-flex align-items-center px-4"
                                                                            style={{
                                                                                border: "1px solid #dee2e6",
                                                                                borderRadius: "0 0.25rem 0.25rem 0rem",
                                                                            }}
                                                                        >
                                                                            {firstToUpper(t("hours"))}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }}
                                                        </Field>
                                                    </Col>

                                                    <Col lg={6}>
                                                        <Field
                                                            label={t("break-duration")}
                                                            name={[name, "breakDuration"]}
                                                            info={t("duration-can-be-left-empty-to-allow-any-duration")}
                                                        >
                                                            {({ value, onChange }) => {
                                                                return (
                                                                    <div className="d-flex">
                                                                        <NumberInput
                                                                            value={value}
                                                                            onChange={(e) => onChange(e.target.value)}
                                                                            className="border-right-0"
                                                                            style={{
                                                                                borderRadius: "0.25rem 0 0 0.25rem",
                                                                            }}
                                                                        />
                                                                        <div
                                                                            className="d-flex align-items-center px-4"
                                                                            style={{
                                                                                border: "1px solid #dee2e6",
                                                                                borderRadius: "0 0.25rem 0.25rem 0rem",
                                                                            }}
                                                                        >
                                                                            {firstToUpper(t("minutes"))}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }}
                                                        </Field>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div>
                                                <p>
                                                    {t("if-a-break-was-not-taken--or-does-not-satisfy-the-rule-give-a-violation")}
                                                    <br />
                                                    {t("if-the-violation-is-eligible-for-a-premium-change-the-pay-code-to-the-premium-pay-code")}
                                                </p>

                                                <Row>
                                                    <Col>
                                                        <Field
                                                            label={t("duration")}
                                                            name={[name, "amount"]}
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: t("required-duration")
                                                                }
                                                            ]}
                                                        >
                                                            {({ value, onChange }) => {
                                                                return (
                                                                    <div className="d-flex">
                                                                        <NumberInput
                                                                            value={value}
                                                                            onChange={(e) => onChange(e.target.value)}
                                                                            className="border-right-0"
                                                                            style={{
                                                                                borderRadius: "0.25rem 0 0 0.25rem",
                                                                            }}
                                                                        />
                                                                        <div
                                                                            className="d-flex align-items-center px-4"
                                                                            style={{
                                                                                border: "1px solid #dee2e6",
                                                                                borderRadius: "0 0.25rem 0.25rem 0rem",
                                                                            }}
                                                                        >
                                                                            {firstToUpper(t("minutes"))}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }}
                                                        </Field>
                                                    </Col>

                                                    {!generateMultiplePremiumsForViolations && (
                                                        <Col>
                                                            <Field
                                                                label={t("violation-pay-code")}
                                                                name={[name, "violationPayCode"]}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: t("required-pay-code")
                                                                    },
                                                                    generatePayCodeValidator(t, data),
                                                                ]}
                                                                dependencies={payCodeFieldDependencies}
                                                            >
                                                                <ResourceSelect
                                                                    renderLabel={renderSettingsResourceLabel}
                                                                    resourcePath={getPathWithParams(
                                                                        "/pay-code",
                                                                        true,
                                                                        {},
                                                                        payCodeCalcFields,
                                                                        {
                                                                            hourType: "unit",
                                                                        }
                                                                    )}
                                                                    hasSearch
                                                                    getSearchFilters={getSettingsResourceSearchFilters}
                                                                    size="small"
                                                                    style={{ width: "auto" }}
                                                                />
                                                            </Field>
                                                        </Col>
                                                    )}

                                                    <Col>
                                                        <Field
                                                            label={t("premium-pay-code")}
                                                            name={[name, "payCode"]}
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: t("required-pay-code")
                                                                },
                                                                generatePayCodeValidator(t, data),
                                                            ]}
                                                            dependencies={payCodeFieldDependencies}
                                                        >
                                                            <ResourceSelect
                                                                renderLabel={renderSettingsResourceLabel}
                                                                resourcePath={getPathWithParams(
                                                                    "/pay-code",
                                                                    true,
                                                                    {},
                                                                    payCodeCalcFields,
                                                                    {
                                                                        hourType: "unit",
                                                                    }
                                                                )}
                                                                hasSearch
                                                                getSearchFilters={getSettingsResourceSearchFilters}
                                                                size="small"
                                                                style={{ width: "auto" }}
                                                            />
                                                        </Field>
                                                    </Col>
                                                </Row>
                                            </div>

                                            {(canWaive || canManagerWaive) && (
                                                <div>
                                                    <div>
                                                        {canWaive && !canManagerWaive && (
                                                            <p>{t("the-violation-can-be-waived-by-an-employee-only-if-the-worked-duration-does-not-exceed")}</p>
                                                        )}
                                                        {!canWaive && canManagerWaive && (
                                                            <p>{t("the-violation-can-be-waived-by-a-manager-only-if-the-worked-duration-does-not-exceed")}</p>
                                                        )}
                                                        {canWaive && canManagerWaive && (
                                                            <p>{t("the-violation-can-be-waived-by-an-employee-or-a-manager-only-if-the-worked-duration-does-not-exceed")}</p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <Row>
                                                            <Col>
                                                                <Field
                                                                    label={t("duration")}
                                                                    name={[name, "maxWaiveDuration"]}
                                                                    rules={[
                                                                        {
                                                                            required: true,
                                                                            message: t("required-duration")
                                                                        }
                                                                    ]}
                                                                >
                                                                    {({ value: _value, onChange }) => {
                                                                        let value;
                                                                        if (!isNaN(Number(_value))) {
                                                                            value = Number(moment.duration(_value, "minutes").asHours().toFixed(2));
                                                                        }
                                                                        return (
                                                                            <div className="d-flex">
                                                                                <NumberInput
                                                                                    value={value}
                                                                                    onChange={(e) => {
                                                                                        let value = e.target.value;
                                                                                        if (!isNaN(Number(value))) {
                                                                                            value = moment.duration(value, "hours").asMinutes();
                                                                                        }
                                                                                        onChange(value)
                                                                                    }}
                                                                                    className="border-right-0"
                                                                                    style={{
                                                                                        borderRadius: "0.25rem 0 0 0.25rem",
                                                                                    }}
                                                                                />
                                                                                <div
                                                                                    className="d-flex align-items-center px-4"
                                                                                    style={{
                                                                                        border: "1px solid #dee2e6",
                                                                                        borderRadius: "0 0.25rem 0.25rem 0rem",
                                                                                    }}
                                                                                >
                                                                                    {firstToUpper(t("hours"))}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }}
                                                                </Field>
                                                            </Col>

                                                            {canWaive && (
                                                                <Col>
                                                                    <Field
                                                                        label={t("employee-waive-pay-code")}
                                                                        name={[name, "waivePayCode"]}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: t("required-pay-code")
                                                                            },
                                                                            generatePayCodeValidator(t, data),
                                                                        ]}
                                                                        dependencies={payCodeFieldDependencies}
                                                                    >
                                                                        <ResourceSelect
                                                                            renderLabel={renderSettingsResourceLabel}
                                                                            resourcePath={getPathWithParams(
                                                                                "/pay-code",
                                                                                true,
                                                                                {},
                                                                                payCodeCalcFields,
                                                                                {
                                                                                    hourType: "unit",
                                                                                }
                                                                            )}
                                                                            hasSearch
                                                                            getSearchFilters={getSettingsResourceSearchFilters}
                                                                            size="small"
                                                                            style={{ width: "auto" }}
                                                                        />
                                                                    </Field>
                                                                </Col>
                                                            )}

                                                            {canManagerWaive && (
                                                                <Col>
                                                                    <Field
                                                                        label={t("manager-waive-pay-code")}
                                                                        name={[name, "managerWaivePayCode"]}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: t("required-pay-code")
                                                                            },
                                                                            generatePayCodeValidator(t, data),
                                                                        ]}
                                                                        dependencies={payCodeFieldDependencies}
                                                                    >
                                                                        <ResourceSelect
                                                                            renderLabel={renderSettingsResourceLabel}
                                                                            resourcePath={getPathWithParams(
                                                                                "/pay-code",
                                                                                true,
                                                                                {},
                                                                                payCodeCalcFields,
                                                                                {
                                                                                    hourType: "unit",
                                                                                }
                                                                            )}
                                                                            hasSearch
                                                                            getSearchFilters={getSettingsResourceSearchFilters}
                                                                            size="small"
                                                                            style={{ width: "auto" }}
                                                                        />
                                                                    </Field>
                                                                </Col>
                                                            )}
                                                        </Row>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {data.length < 5 && (
                                    <Button color="muted" className="text-primary border-primary" onClick={() => add()}>
                                        <FontAwesomeIcon icon={faPlus} />
                                        {" "}
                                        {t("add-meal-requirement")}
                                    </Button>
                                )}
                            </div>
                        );
                    }}
                </List>
            </div>

            <div className="d-flex justify-content-end">
                <Button
                    onClick={close}
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                >
                    {t("cancel")}
                </Button>

                <Button
                    onClick={goBack}
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                >
                    {t("back")}
                </Button>

                {(!disabled && isMasterCompany) && (
                    <Button
                        type="submit"
                        className="btn-dark btn-sm shadow-none"
                    >
                        {t("submit")}
                    </Button>
                )}
            </div>
        </Form>
    );
};

export default Rules;
